import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { FileDTO } from '../../dtos/file.dto';

export interface Conversation {
  id: number;
  user_id: number;
  title: string;
  created_at: number;
  updated_at: number;
  glai_stream_id: string;
}

export interface ConversationResponse {
  item: Conversation;
}

export interface TokenData {
  content: string;
  message_id: string;
  timestamp: string;
  model_id: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface ChatMessage {
  id: string;
  content: string;
  thinking?: string;
  isComplete: boolean;
  timestamp: string;
  role: 'user' | 'assistant';
  files?: FileDTO[];
}

@Injectable({
  providedIn: 'root',
})
export class GlaiService implements OnDestroy {
  private messageSubject = new Subject<ChatMessage>();

  public messages$ = this.messageSubject.asObservable();

  private eventSource: EventSource | null = null;

  private currentMessages = new Map<string, ChatMessage>();

  constructor(private apiService: APICoreService) {}

  addUserMessage(content: string, uploadedFiles: FileDTO[]): void {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content,
      isComplete: true,
      timestamp: new Date().toISOString(),
      role: 'user',
      files: uploadedFiles,
    };
    this.messageSubject.next(userMessage);
  }

  startConversation(
    message: string,
    messageFiles?: FileDTO[],
    modes?: string[]
  ): Observable<ConversationResponse> {
    return this.apiService.postJSONRequest('ai/conversation', {
      message,
      files: messageFiles,
      modes,
    });
  }

  continueConversation(
    conversationId: number,
    message: string,
    messageFiles?: FileDTO[],
    modes?: string[]
  ): Observable<unknown> {
    return this.apiService.postJSONRequest(
      `ai/conversation/${conversationId}/message`,
      { message, files: messageFiles, modes }
    );
  }

  connectToStream(streamId: string): void {
    this.disconnectStream();

    const glaiApiUrl = EnvironmentService.glaiApiUrl();
    const streamUrl = `${glaiApiUrl}/chatbot/stream/${streamId}`;

    this.eventSource = new EventSource(streamUrl);

    this.eventSource.addEventListener('token', (event: MessageEvent) => {
      try {
        const tokenData: TokenData = JSON.parse(event.data);
        this.handleTokenMessage(tokenData);
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    });

    this.eventSource.addEventListener('error', (event: MessageEvent) => {
      try {
        const tokenData: TokenData = JSON.parse(event.data);
        this.handleTokenMessage(tokenData);
      } catch (error) {
        console.error('Error parsing SSE message:', error);
      }
    });

    this.eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
    };
  }

  private handleTokenMessage(tokenData: TokenData): void {
    const messageId = tokenData.message_id;

    if (this.currentMessages.has(messageId)) {
      const existingMessage = this.currentMessages.get(messageId);
      if (existingMessage) {
        existingMessage.content += tokenData.content;
        GlaiService.extractThinking(existingMessage);
        this.messageSubject.next({ ...existingMessage });
      }
    } else {
      const newMessage: ChatMessage = {
        id: messageId,
        content: tokenData.content,
        isComplete: false,
        timestamp: tokenData.timestamp,
        role: 'assistant',
      };
      GlaiService.extractThinking(newMessage);
      this.currentMessages.set(messageId, newMessage);
      this.messageSubject.next(newMessage);
    }
  }

  static extractThinking(message: ChatMessage): void {
    const thinkingMatch = message.content.match(/<thinking>(.*?)<\/thinking>/s);
    if (thinkingMatch) {
      message.thinking = thinkingMatch[1].trim();
      message.content = message.content
        .replace(/<thinking>.*?<\/thinking>/s, '')
        .trim();
    }
  }

  markMessageComplete(messageId: string): void {
    if (this.currentMessages.has(messageId)) {
      const message = this.currentMessages.get(messageId);
      if (message) {
        message.isComplete = true;
        this.messageSubject.next({ ...message });
      }
    }
  }

  disconnectStream(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  ngOnDestroy(): void {
    this.disconnectStream();
  }
}
